import { Instance, types } from 'mobx-state-tree';

import { createMapWithTransform } from 'utils/store/createMap';
import mandatoryId from 'utils/store/mandatoryId';
import BenchmarkModel, { BenchmarkModelType } from './BenchmarkModel';
import BriefingModel from './BriefingModel';
import FireTaskModel, { createFireTaskModel, FireTaskModelType } from './FireTaskModel';
import HypothesisModel, { HypothesisModelType } from './HypothesisModel';
import PainpointModel from './PainpointModel';
import { ClusterPainpoints } from './PainpointsStore';
import { createFluidUserModel, FluidUserModel } from './UserModel';

export const StatusEnum = types.enumeration([
  'created',
  'running',
  'done',
  'failed'
]);
export type Status = Instance<typeof StatusEnum>;

export const fireTypeOptions = [
  'ideation',
  'benchmarking',
  'brainstorming'
];
export const FireTypeEnum = types.enumeration(fireTypeOptions);
export type FireType = Instance<typeof FireTypeEnum>;

export const FireModel = types.model('FireModel', {
  id: types.identifierNumber,
  author: types.maybe(FluidUserModel),
  input: types.maybe(types.string),
  status: types.maybe(StatusEnum),
  fire_type: types.maybe(FireTypeEnum),
  target_group_description: types.maybe(types.string),
  service_description: types.maybe(types.string),
  business_target_description: types.maybe(types.string),
  created_at: types.maybe(types.string),
  fire_tasks: types.map(FireTaskModel),
  painpoints: types.map(types.reference(PainpointModel)),
  benchmarks: types.map(types.reference(BenchmarkModel)),
  hypothesis: types.map(types.reference(HypothesisModel)),
  has_user_settings: types.maybe(types.boolean),
  needs_user_settings: types.maybe(types.boolean),
  briefing: types.maybe(types.reference(BriefingModel)),
  project_id: types.maybe(types.number),
  fire_config_id: types.maybe(types.number),
  fire_strategy: types.maybe(types.string),
  fire_strategy_icon: types.maybe(types.string),
  project_name: types.maybe(types.string),
  inital_cluster_amount: types.maybe(types.number),
  inital_benchmark_amount: types.maybe(types.number),
  inital_hypothesis_amount: types.maybe(types.number),
  powerpoint_url: types.maybe(types.string)
}).actions((self) => ({
  clearPowerpointUrl() {
    self.powerpoint_url = undefined;
  },
  setPowerpointUrl(url: string | undefined) {
    self.powerpoint_url = url;
  }
})).views((self) => {
  return {
    get sortedFireTasks(): FireTaskModelType[] {
      const tasks = Array.from(self.fire_tasks.values());
      return tasks.sort((a, b) => {
        if (a.position !== undefined && b.position !== undefined) {
          return a.position - b.position;
        }
        return a.id - b.id;
      });
    },
    get allTasksDone(): boolean {
      return this.sortedFireTasks.every((task) => task.status === 'done');
    },
    get painpointsByCluster(): ClusterPainpoints[] {
      const clusterPainpoints: ClusterPainpoints[] = [];

      if (!self.painpoints) {
        return clusterPainpoints;
      }

      for (const painpoint of self.painpoints.values()) {
        if (painpoint.cluster) {
          const cluster = clusterPainpoints.find(cp => cp.cluster?.id === painpoint.cluster!.id);
          if (cluster) {
            cluster.painpoints.push(painpoint);
          } else {
            clusterPainpoints.push({
              cluster: painpoint.cluster,
              painpoints: [painpoint]
            });
          }
        } else {
          clusterPainpoints.push({
            cluster: undefined,
            painpoints: [painpoint]
          });
        }
      }
      return clusterPainpoints;
    },
    get benchmarkList(): BenchmarkModelType[] {
      const benchmarks: BenchmarkModelType[] = [];

      if (!self.benchmarks) {
        return benchmarks;
      }

      for (const benchmark of self.benchmarks.values()) {
        benchmarks.push(benchmark);
      }
      return benchmarks;
    },
    get hypothesisList(): HypothesisModelType[] {
      const hypotheses: HypothesisModelType[] = [];

      if (!self.hypothesis) {
        return hypotheses;
      }

      for (const hypothesis of self.hypothesis.values()) {
        hypotheses.push(hypothesis);
      }
      return hypotheses;
    },
    get painpoints_count(): number {
      return self.painpoints?.size || 0;
    },
    get benchmarks_count(): number {
      return self.benchmarks?.size || 0;
    },
    get hypotheses_count(): number {
      return self.hypothesis?.size || 0;
    },
    isEditable(userId: number): boolean {
      return self.author?.id === userId;
    }
  }
});


export const createFireModel = (data?: any): FireModelType | undefined => {
  if (!data) {
    return undefined;
  }

  return FireModel.create({
    id: mandatoryId(data?.id),
    author: data?.author ? createFluidUserModel(data.author) : undefined,
    input: data?.input || undefined,
    status: data?.status ? StatusEnum.create(data.status) : undefined,
    fire_type: data?.fire_type ? FireTypeEnum.create(data.fire_type) : undefined,
    target_group_description: data?.target_group_description || undefined,
    service_description: data?.service_description || undefined,
    business_target_description: data?.business_target_description || undefined,
    created_at: data?.created_at || undefined,
    fire_tasks: data?.fire_tasks ? createMapWithTransform(data.fire_tasks, createFireTaskModel) : undefined,
    painpoints: data?.painpoints ? createMapWithTransform(data.painpoints, (p) => p.id) : undefined,
    benchmarks: data?.benchmarks ? createMapWithTransform(data.benchmarks, (b) => b.id) : undefined,
    hypothesis: data?.hypothesis ? createMapWithTransform(data.hypothesis, (h) => h.id) : undefined,
    has_user_settings: data?.has_user_settings || undefined,
    needs_user_settings: data?.needs_user_settings || undefined,
    briefing: data?.briefing?.id || undefined,
    project_id: data?.project?.id || undefined,
    fire_config_id: data?.fire_config_id || undefined,
    fire_strategy: data?.fire_strategy || undefined,
    fire_strategy_icon: data?.fire_strategy_icon || undefined,
    project_name: data?.project_name || undefined,
    inital_cluster_amount: data?.inital_cluster_amount || undefined,
    inital_benchmark_amount: data?.inital_benchmark_amount || undefined,
    inital_hypothesis_amount: data?.inital_hypothesis_amount || undefined,
    powerpoint_url: data?.powerpoint_url || undefined
  });
};

export type FireModelType = typeof FireModel.Type;
export default FireModel;
